import { useState, useMemo, useEffect } from 'react';
import { Popup, Anchor, useMap } from 'react-map-gl';
import { useAtom } from 'jotai';
import { Loading } from '@carbon/react';
import { DrillingRigQueryResponse, DrillingRig } from 'types/api-responses';
import searchAtomPair, { SearchParams } from 'atoms/search-atom';
import baseStyle from '../map.module.scss';
import drillingRigStyle from './driling-rig-popup.module.scss';

interface DrillingRigPopupProps {
  closeButton?: boolean;
  onClose: () => void;
  latitude: number;
  longitude: number;
  rigId: number;
  uwi: string;
}

interface DrillingRigQueryParams extends SearchParams {
  rigId: number;
}

const createDrillingRigPopupSearchAtom = (rigId: number) => {
  return searchAtomPair<DrillingRigQueryResponse, DrillingRigQueryParams>(
    { href: '/queries/drilling_rigs' },
    {
      rigId: rigId,
    }
  );
};

const DrillingRigPopup = ({
  closeButton = true,
  onClose,
  latitude,
  longitude,
  rigId,
  uwi,
}: DrillingRigPopupProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [currentRigId, setCurrentRigId] = useState<number | null>(null);

  // Popup positioning
  const { current: map } = useMap();
  const [anchor, setAnchor] = useState<Anchor>('right');

  const searchAtoms = useMemo(
    () => createDrillingRigPopupSearchAtom(rigId),
    [rigId]
  );
  const [searchData, triggerSearch] = useAtom(searchAtoms.searchAtom);

  const drillingRig = useMemo(() => {
    const drillingRigData = searchData.data?.results?.[0];
    return drillingRigData || null;
  }, [searchData.data?.results]) as DrillingRig | null;

  useEffect(() => {
    if (currentRigId === rigId && searchData.data?.results?.length) {
      return;
    }

    const timeout = setTimeout(() => {
      triggerSearch(() => ({
        page: 1,
        pageSize: 1,
        rigId: rigId,
      }));
      setCurrentRigId(rigId);
      setIsExpanded(true);
    }, 500);

    return () => {
      clearTimeout(timeout);
    };
  }, [rigId, currentRigId, searchData.data?.results?.length, triggerSearch]);

  // Calculate popup anchor position
  useEffect(() => {
    if (!map || !isExpanded) return;

    const mapContainer = map.getContainer();
    const { width, height } = mapContainer.getBoundingClientRect();
    const point = map.project([longitude, latitude]);
    const popupWidth = 450; // Match the actual popup width (400-500px)
    const popupHeight = 400; // Increased to account for the content height
    const buffer = 10;
    const spaceOnRight = width - point.x - buffer;
    const spaceOnBottom = height - point.y - buffer;

    let horizontalAnchor: string;
    if (spaceOnRight < popupWidth) {
      horizontalAnchor = 'right';
    } else {
      horizontalAnchor = 'left';
    }

    let verticalAnchor: string;
    if (spaceOnBottom < popupHeight) {
      verticalAnchor = 'bottom';
    } else {
      verticalAnchor = 'top';
    }

    const finalAnchor: Anchor = verticalAnchor
      ? (`${verticalAnchor}-${horizontalAnchor}` as Anchor)
      : (horizontalAnchor as Anchor);

    setAnchor(finalAnchor);
  }, [map, longitude, latitude, isExpanded]);

  if (searchData.error) {
    return (
      <Popup
        key={Number(closeButton)}
        closeButton={closeButton}
        closeOnClick={false}
        onClose={onClose}
        latitude={latitude}
        longitude={longitude}
        anchor={anchor}
        className={`${baseStyle.popup} ${baseStyle.drillingRigPopup}`}
      >
        <div className={drillingRigStyle.popupContainer}>
          <h3>Error</h3>
          <p>{searchData.error}</p>
        </div>
      </Popup>
    );
  }

  return (
    <Popup
      key={Number(closeButton)}
      closeButton={closeButton}
      closeOnClick={false}
      onClose={onClose}
      latitude={latitude}
      longitude={longitude}
      anchor={anchor}
      className={`${baseStyle.popup} ${baseStyle.drillingRigPopup} ${
        !isExpanded ? baseStyle.drillingRigPopupCollapsed : ''
      }`}
    >
      <div className={drillingRigStyle.popupHeader}>
        {!isExpanded && (
          <div>
            <p className={drillingRigStyle.drillingRigTitle}>{uwi}</p>
          </div>
        )}
      </div>
      <div className={drillingRigStyle.popupContainer}>
        {isExpanded && (
          <>
            <div className={drillingRigStyle.drillingRigHeader}>
              <div>
                <h3 className={drillingRigStyle.drillingRigTitle}>
                  DRILLING RIG
                </h3>
              </div>
            </div>
            <div className={drillingRigStyle.drillingRigHeader}>
              <div>
                <h3 className={drillingRigStyle.drillingRigTitle}>{uwi}</h3>
              </div>
            </div>
            {searchData.loading && (
              <div className={drillingRigStyle.drillingRigLoading}>
                <Loading small description="Loading drilling rig data..." />
              </div>
            )}

            {!searchData.loading && drillingRig && (
              <>
                <div className={drillingRigStyle.drillingRigContainer}>
                  <div className={drillingRigStyle.drillingRigDetails}>
                    <div className={drillingRigStyle.drillingRigRow}>
                      <span className={drillingRigStyle.drillingRigLabel}>
                        UWI:
                      </span>
                      <span className={drillingRigStyle.drillingRigValue}>
                        <a
                          href={
                            uwi ? `${drillingRig.resources.well.href}` : '#'
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {uwi || '--'}
                        </a>
                      </span>
                    </div>
                    <div className={drillingRigStyle.drillingRigRow}>
                      <span className={drillingRigStyle.pipelineLabel}>
                        Well Name-Well Number:
                      </span>
                      <span className={drillingRigStyle.drillingRigValue}>
                        {drillingRig.wellName || '--'}
                      </span>
                    </div>
                    <div className={drillingRigStyle.drillingRigRow}>
                      <span className={drillingRigStyle.drillingRigLabel}>
                        Spud Date:
                      </span>
                      <span className={drillingRigStyle.drillingRigValue}>
                        {drillingRig.spudDate || '--'}
                      </span>
                    </div>
                    <div className={drillingRigStyle.drillingRigRow}>
                      <span className={drillingRigStyle.drillingRigLabel}>
                        Operator:
                      </span>
                      <span className={drillingRigStyle.drillingRigValue}>
                        {drillingRig.operator || '--'}
                      </span>
                    </div>
                    <div className={drillingRigStyle.drillingRigRow}>
                      <span className={drillingRigStyle.drillingRigLabel}>
                        Current Depth:
                      </span>
                      <span className={drillingRigStyle.drillingRigValue}>
                        {drillingRig.currentDepth || '--'}
                      </span>
                    </div>
                    <div className={drillingRigStyle.drillingRigRow}>
                      <span className={drillingRigStyle.drillingRigLabel}>
                        Drilling Contractor:
                      </span>
                      <span className={drillingRigStyle.drillingRigValue}>
                        {drillingRig.contractor || '--'}
                      </span>
                    </div>
                    <div className={drillingRigStyle.drillingRigRow}>
                      <span className={drillingRigStyle.drillingRigLabel}>
                        Rig Name:
                      </span>
                      <span className={drillingRigStyle.drillingRigValue}>
                        {drillingRig.rigName || '--'}
                      </span>
                    </div>
                    <div className={drillingRigStyle.drillingRigRow}>
                      <span className={drillingRigStyle.drillingRigLabel}>
                        Rig Status:
                      </span>
                      <span className={drillingRigStyle.drillingRigValue}>
                        {drillingRig.rigStatus || '--'}
                      </span>
                    </div>
                    <div className={drillingRigStyle.drillingRigRow}>
                      <span className={drillingRigStyle.drillingRigLabel}>
                        Rig Status Detail:
                      </span>
                      <span className={drillingRigStyle.drillingRigValue}>
                        {drillingRig.rigStatusDetail || '--'}
                      </span>
                    </div>
                    <div className={drillingRigStyle.drillingRigRow}>
                      <span className={drillingRigStyle.drillingRigLabel}>
                        Target Fluid:
                      </span>
                      <span className={drillingRigStyle.drillingRigValue}>
                        {drillingRig.targetFluid || '--'}
                      </span>
                    </div>
                  </div>
                </div>
              </>
            )}

            {!searchData.loading && !drillingRig && currentRigId === rigId && (
              <div className={drillingRigStyle.noDrillingRigMessage}>
                No drilling rig data found.
              </div>
            )}
          </>
        )}
      </div>
    </Popup>
  );
};

export { DrillingRigPopup };
